/**
 * Turns a raw attribute bag plus a pop-up configuration into what the pop-up
 * card actually shows.
 *
 * Pure functions, no React and no services, so the map runtime and the panel's
 * live preview render through EXACTLY the same code — a preview that agreed
 * with the map only by coincidence would be worse than no preview.
 *
 * When a layer has no configuration these functions are not called at all; the
 * map falls back to `formatFeatureForPopup`, which is the pre-existing
 * behaviour.
 */

import {
  formatFieldValue,
  humanizeFieldName,
  isHiddenField,
  type FormattedField,
} from '../utils/featureFormat';
import { popupFieldsBlock, type LayerPopup, type PopupFieldFormat } from './PopupTypes';

/** `{field}` tokens, matching the labelling expression syntax. */
const TOKEN = /\{([^{}]+)\}/g;

/** Case-insensitive attribute lookup — services disagree about column casing. */
function lookup(attributes: Record<string, unknown>, field: string): unknown {
  if (field in attributes) return attributes[field];
  const needle = field.trim().toLowerCase();
  const key = Object.keys(attributes).find((name) => name.trim().toLowerCase() === needle);
  return key === undefined ? undefined : attributes[key];
}

/**
 * Applies a field's formatting.
 *
 * Falls through to the shared formatter whenever no formatting is configured,
 * so an unformatted field in a configured pop-up looks identical to the same
 * field in an automatic one.
 */
export function formatConfiguredValue(
  fieldName: string,
  raw: unknown,
  format: PopupFieldFormat | undefined,
): string | null {
  const affix = (text: string) => `${format?.prefix ?? ''}${text}${format?.suffix ?? ''}`;

  if (raw === null || raw === undefined) return null;

  if (format?.dateFormat) {
    // Epoch milliseconds (ArcGIS) and ISO strings (GeoServer) both land here.
    const date = new Date(typeof raw === 'number' ? raw : String(raw));
    if (!Number.isNaN(date.getTime())) {
      return affix(
        format.dateFormat === 'datetime' ? date.toLocaleString() : date.toLocaleDateString(),
      );
    }
  }

  const numeric = typeof raw === 'number' ? raw : undefined;
  if (numeric !== undefined && !Number.isNaN(numeric)) {
    const hasNumberFormat = format?.decimals !== undefined || format?.thousands !== undefined;
    if (hasNumberFormat) {
      return affix(
        new Intl.NumberFormat(undefined, {
          minimumFractionDigits: format?.decimals,
          maximumFractionDigits: format?.decimals,
          useGrouping: format?.thousands === true,
        }).format(numeric),
      );
    }
  }

  const fallback = formatFieldValue(fieldName, raw);
  return fallback === null ? null : affix(fallback);
}

/**
 * The pop-up heading.
 *
 * Substitution only — a token names a field, nothing is evaluated. A token with
 * no matching value collapses to nothing, and a template that ends up empty
 * falls back to the layer name rather than showing a blank header.
 */
export function resolvePopupTitle(
  template: string,
  attributes: Record<string, unknown>,
  fallback: string,
): string {
  const trimmed = (template ?? '').trim();
  if (!trimmed) return fallback;

  const resolved = trimmed
    .replace(TOKEN, (_match, field: string) => {
      const value = formatFieldValue(field.trim(), lookup(attributes, field.trim()));
      return value ?? '';
    })
    .replace(/\s{2,}/g, ' ')
    .trim();

  return resolved || fallback;
}

/**
 * The attribute rows, in configured order.
 *
 * Two deliberate safety valves:
 *  - a configuration whose field list is empty shows every attribute, so a
 *    layer whose schema could not be read never renders an empty pop-up;
 *  - a field whose value is null or blank is skipped unless the configuration
 *    asks for it, matching the automatic pop-up by default rather than
 *    printing a column of empty rows.
 */
export function resolvePopupFields(
  attributes: Record<string, unknown>,
  aliases: Record<string, string> | undefined,
  popup: LayerPopup,
): FormattedField[] {
  const block = popupFieldsBlock(popup);
  const labelFor = (field: string, custom?: string) => {
    if (custom && custom.trim()) return custom.trim();
    const alias = aliases?.[field];
    if (alias && alias.trim()) return alias.trim();
    return humanizeFieldName(field);
  };

  if (block.fields.length === 0) {
    return Object.entries(attributes ?? {})
      .filter(([field]) => !isHiddenField(field))
      .map(([field, raw]) => ({ label: labelFor(field), value: formatFieldValue(field, raw) }))
      .filter((entry): entry is FormattedField => entry.value !== null);
  }

  const rows: FormattedField[] = [];
  const used = new Set<string>();

  for (const config of block.fields) {
    used.add(config.field.trim().toLowerCase());
    if (!config.visible) continue;

    const value = formatConfiguredValue(config.field, lookup(attributes, config.field), config.format);
    if (value === null && !block.showEmpty) continue;
    // The card renders an empty string as its own "no value" mark.
    rows.push({ label: labelFor(config.field, config.label), value: value ?? '' });
  }

  // Columns this configuration has never seen — only when explicitly allowed.
  if (block.includeUnlisted) {
    for (const [field, raw] of Object.entries(attributes ?? {})) {
      if (used.has(field.trim().toLowerCase()) || isHiddenField(field)) continue;
      const value = formatFieldValue(field, raw);
      if (value === null) continue;
      rows.push({ label: labelFor(field), value });
    }
  }

  return rows;
}
